import React from "react";
import type { GenericRCAFormData } from "./types";

interface RCAIntroductionSectionProps {
  formData: GenericRCAFormData;
  onChange: (field: keyof GenericRCAFormData, value: any) => void;
  readOnly: boolean;
  regions: any[];
  premises: any[];
}

const RCAIntroductionSection: React.FC<RCAIntroductionSectionProps> = ({
  formData,
  onChange,
  readOnly,
  regions,
  premises,
}) => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">A. Introduction</h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">
            Region <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.region}
            onChange={(e) => onChange("region", e.target.value)}
            className="w-full p-2 border rounded"
            required
            disabled={readOnly}
          >
            <option value="">Select Region</option>
            {regions.map((region: any) => (
              <option key={region._id} value={region._id}>
                {region.regionName}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            Premises Name <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.premisesName}
            onChange={(e) => onChange("premisesName", e.target.value)}
            className="w-full p-2 border rounded"
            required
            disabled={readOnly}
          >
            <option value="">Select Premises</option>
            {premises
              .filter((p: any) =>
                p.region === formData.region ||
                p.region?._id === formData.region ||
                p.region?.regionName === formData.region
              )
              .map((premise: any) => (
                <option key={premise._id} value={premise._id}>
                  {premise.premisesName}
                </option>
              ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            Type of Incident Event <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.incidentEventType}
            onChange={(e) =>
              onChange("incidentEventType", e.target.value as any)
            }
            className="w-full p-2 border rounded"
            required
            disabled={readOnly}
          >
            <option value="">Select Type</option>
            <option value="Fire">Fire</option>
            <option value="Work At Height">Work At Height</option>
            <option value="Confined Space">Confined Space</option>
            <option value="Slip/ Trip Hazard">Slip/ Trip Hazard</option>
            <option value="Chemical Spillage">Chemical Spillage</option>
            <option value="Hazardous waste">Hazardous waste</option>
            <option value="Clinical Incident">Clinical Incident</option>
            <option value="Non-Clinical Incident">Non-Clinical Incident</option>
            <option value="Safety Incident">Safety Incident (Fire & General Safety)</option>
            <option value="Security Incident">Security Incident</option>
            <option value="Sentinel Event">Sentinel Event</option>
            <option value="Near Miss Incident">Near Miss Incident</option>
            <option value="Code-based Incidents">Disaster / Code-based Incidents</option>
            <option value="Other">Other</option>
          </select>
        </div>
        {formData.incidentEventType === "Other" && (
          <div>
            <label className="block text-sm font-medium mb-1">
              Specify Incident Type <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.incidentEventTypeOther}
              onChange={(e) => onChange("incidentEventTypeOther", e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="Describe the incident type"
              readOnly={readOnly}
            />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium mb-1">
            Date & Time of Event <span className="text-red-500">*</span>
          </label>
          <input
            type="datetime-local"
            value={formData.eventDateTime}
            onChange={(e) => onChange("eventDateTime", e.target.value)}
            className="w-full p-2 border rounded"
            required
            readOnly={readOnly}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            Date of RCA Done
          </label>
          <input
            type="date"
            value={formData.rcaDoneDate}
            onChange={(e) => onChange("rcaDoneDate", e.target.value)}
            className="w-full p-2 border rounded"
            readOnly={readOnly}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Location</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => onChange("location", e.target.value)}
            className="w-full p-2 border rounded"
            readOnly={readOnly}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Severity</label>
          <select
            value={formData.severity}
            onChange={(e) => onChange("severity", e.target.value as any)}
            className="w-full p-2 border rounded"
            disabled={readOnly}
          >
            <option value="">Select Severity</option>
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
            <option value="Critical">Critical</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Reported By</label>
          <input
            type="text"
            value={formData.reportedBy}
            onChange={(e) => onChange("reportedBy", e.target.value)}
            className="w-full p-2 border rounded"
            readOnly={readOnly}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <select
            value={formData.status}
            onChange={(e) => onChange("status", e.target.value as any)}
            className="w-full p-2 border rounded"
            disabled={readOnly}
          >
            <option value="Draft">Draft</option>
            <option value="In Progress">In Progress</option>
            <option value="Under Review">Under Review</option>
            <option value="Completed">Completed</option>
            <option value="Closed">Closed</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default RCAIntroductionSection;
